/* ==========================================================================
   Alwayz Clean Lab - collection.js
   <collection-filters>: AJAX filtering / sorting / "load more" with the
   Section Rendering API, filter drawer, grid density toggle, <price-range>.
   Depends on global.js.
   ========================================================================== */
(() => {
  'use strict';
  const A = window.Alwayz;
  const { $, $$, define, debounce, fetchHTML, parseHTML, lockScroll, unlockScroll } = A;

  class PriceRange extends HTMLElement {
    connectedCallback() {
      this.max = +this.dataset.max || 1000;
      this.lo = $('[data-prange-lo]', this);
      this.hi = $('[data-prange-hi]', this);
      this.minIn = $('[data-prange-min-input]', this);
      this.maxIn = $('[data-prange-max-input]', this);
      this.minHidden = $('[data-prange-min-hidden]', this);
      this.maxHidden = $('[data-prange-max-hidden]', this);
      const sync = (from) => {
        let a = +this.lo.value, b = +this.hi.value;
        if (from === 'lo' && a > b) { b = a; this.hi.value = b; }
        if (from === 'hi' && b < a) { a = b; this.lo.value = a; }
        this.minIn.value = a; this.maxIn.value = b;
        this.style.setProperty('--lo', `${(a / this.max) * 100}%`);
        this.style.setProperty('--hi', `${(b / this.max) * 100}%`);
      };
      const commit = () => {
        const a = +this.lo.value, b = +this.hi.value;
        // The URL params (filter.v.price.gte / .lte) take whole currency units (rupees), not paise.
        this.minHidden.value = a > 0 ? a : '';
        this.maxHidden.value = b < this.max ? b : '';
        this.dispatchEvent(new Event('change', { bubbles: true }));
      };
      this.lo.addEventListener('input', () => sync('lo'));
      this.hi.addEventListener('input', () => sync('hi'));
      this.lo.addEventListener('change', (e) => { e.stopPropagation(); commit(); });
      this.hi.addEventListener('change', (e) => { e.stopPropagation(); commit(); });
      this.minIn.addEventListener('change', (e) => { e.stopPropagation(); this.lo.value = Math.min(+this.minIn.value || 0, +this.hi.value); sync('lo'); commit(); });
      this.maxIn.addEventListener('change', (e) => { e.stopPropagation(); this.hi.value = Math.max(+this.maxIn.value || this.max, +this.lo.value); sync('hi'); commit(); });
    }
  }
  define('price-range', PriceRange);

  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.abort = null;
      this.bind();
      window.addEventListener('popstate', () => this.fetchAndRender(location.search.slice(1), false));
      this.initDensity();
    }
    bind() {
      // Use event delegation so it survives re-renders
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-facets-form]') || e.target.matches('[data-sort]')) this.apply();
      });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (link) { e.preventDefault(); const u = new URL(link.href, location.origin); this.fetchAndRender(u.search.slice(1), true); return; }
        if (e.target.closest('[data-filters-open]')) { this.classList.add('is-open'); lockScroll(); return; }
        if (e.target.closest('[data-filters-close]')) { this.closeFilters(); return; }
        const more = e.target.closest('[data-load-more-btn]');
        if (more) { e.preventDefault(); this.loadMore(more); }
      });
      this.addEventListener('submit', (e) => { if (e.target.closest('[data-facets-form]')) e.preventDefault(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.closeFilters(); });
      this.addEventListener('click', (e) => {
        const d = e.target.closest('[data-density]');
        if (d) this.setDensity(+d.dataset.density);
      });
    }
    closeFilters() { if (this.classList.contains('is-open')) { this.classList.remove('is-open'); unlockScroll(); } }
    params() {
      const form = $('[data-facets-form]', this);
      const params = new URLSearchParams();
      if (form) new FormData(form).forEach((v, k) => { if (v !== '' && v != null) params.append(k, v); });
      const sort = $('[data-sort]', this);
      if (sort && sort.value) params.set('sort_by', sort.value);
      return params;
    }
    apply() { clearTimeout(this._t); this._t = setTimeout(() => this.fetchAndRender(this.params().toString(), true), 120); }
    async fetchAndRender(query, push) {
      this.abort && this.abort.abort();
      this.abort = new AbortController();
      const results = $('[data-coll-results]', this);
      results && results.classList.add('is-loading');
      const url = `${this.dataset.url}?${query}${query ? '&' : ''}section_id=${this.sectionId}`;
      try {
        const res = await fetch(url, { signal: this.abort.signal });
        if (!res.ok) throw new Error(res.statusText);
        const doc = parseHTML(await res.text());
        const next = $('collection-filters', doc);
        if (!next) throw new Error('No results');
        ['[data-coll-results]', '[data-facets]', '[data-coll-count]', '[data-filters-open]'].forEach((sel) => {
          const a = $(sel, this);
          const b = $(sel, next);
          if (a && b) a.replaceWith(b);
        });
        // keep the sort <select> value in sync
        const sort = $('[data-sort]', this); const sortNext = $('[data-sort]', next);
        if (sort && sortNext) sort.value = sortNext.value;
        if (push) history.pushState({}, '', `${this.dataset.url}${query ? `?${query}` : ''}`);
        this.applyDensity();
        A.reveal && A.reveal.scan(this);
        const top = this.getBoundingClientRect().top + window.scrollY - (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sticky-top')) || 96);
        if (window.scrollY > top + 200) window.scrollTo({ top, behavior: A.reduceMotion ? 'auto' : 'smooth' });
      } catch (err) {
        if (err.name !== 'AbortError') { results && results.classList.remove('is-loading'); A.toast(A.strings.error); }
      }
    }
    async loadMore(btn) {
      const wrap = btn.closest('[data-load-more]');
      btn.classList.add('is-loading');
      try {
        const u = new URL(btn.href, location.origin);
        u.searchParams.set('section_id', this.sectionId);
        const doc = parseHTML(await fetchHTML(u.toString()));
        const grid = $('[data-product-grid]', this);
        const cells = $$('[data-product-grid] .coll__cell', doc);
        cells.forEach((c) => grid.appendChild(document.importNode(c, true)));
        const nextWrap = $('[data-load-more]', doc);
        if (nextWrap) wrap.replaceWith(document.importNode(nextWrap, true)); else wrap.remove();
        A.reveal && A.reveal.scan(grid);
        const nextUrl = $('[data-load-more-btn]', doc);
        // shareable URL keeps the loaded page number
        const pageParam = new URL(btn.href, location.origin).searchParams.get('page');
        if (pageParam) { const cur = new URL(location.href); cur.searchParams.set('page', pageParam); history.replaceState({}, '', cur); }
      } catch (err) { btn.classList.remove('is-loading'); A.toast(A.strings.error); }
    }

    /* grid density */
    initDensity() {
      let saved = null;
      try { saved = +localStorage.getItem('alwayz:density'); } catch (e) { /* noop */ }
      this.density = saved || 0;
      this.applyDensity();
    }
    setDensity(n) {
      this.density = n;
      try { localStorage.setItem('alwayz:density', String(n)); } catch (e) { /* noop */ }
      this.applyDensity();
    }
    applyDensity() {
      const grid = $('[data-product-grid]', this);
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const tablet = window.matchMedia('(max-width: 989px)').matches;
      if (grid && this.density) {
        if (mobile) grid.style.setProperty('--cols-m', Math.min(this.density, 2));
        else if (tablet) grid.style.setProperty('--cols-t', Math.min(this.density, 3));
        else grid.style.setProperty('--cols-d', this.density);
      }
      const shown = this.density || +($('[data-product-grid]', this)?.style.getPropertyValue('--cols-d')) || 4;
      $$('[data-density]', this).forEach((b) => b.setAttribute('aria-pressed', String(+b.dataset.density === (mobile ? Math.min(shown, 2) : shown))));
    }
  }
  define('collection-filters', CollectionFilters);
})();
